import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FACET_KEYS, NO_FILTERS } from '../../domain/cars/carFilters';

/**
 * What the garage was showing when you left it.
 *
 * The filters live in the URL QUERY rather than in a module-level record, which
 * is the one place this diverges from the sibling product. Two reasons, and the
 * second is the one that decided it:
 *
 *   - A filtered garage is worth SHARING. `?decade=90s&origin=jp` is a shelf
 *     somebody assembled, and a link that reopens the unfiltered catalogue is a
 *     link that lost the only interesting thing about it.
 *   - The deck on the detail screen is the filtered shelf the reader arrived
 *     from (`domain/cars/carDeck.js`). That state has to survive a navigation to
 *     another route, and the URL is the only thing here that does. Held in a
 *     module record it would also be invisible in the address bar while silently
 *     changing what a swipe deals.
 *
 * The SCROLL position stays a module record, and deliberately: it is a fact
 * about this browsing session and nobody means to send it. A shared link that
 * reopens 400 pixels down is a link that looks broken.
 */

/**
 * Values arrive from a URL anybody can type, so they are read as data and never
 * trusted: `carFilters` matches a value against the catalogue and an unknown one
 * simply matches nothing. What IS guarded here is the SHAPE — every facet key
 * present, every value a string — because the rest of the app treats the filter
 * object as total.
 */
export function readFiltersFromSearch(search) {
    const params = new URLSearchParams(search || '');

    return FACET_KEYS.reduce((filters, key) => {
        const values = (params.get(key) || '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);

        return { ...filters, [key]: values };
    }, { ...NO_FILTERS });
}

/**
 * The query a filtered garage is addressed by.
 *
 * Comma-separated within a facet rather than repeated keys: `?decade=80s,90s` is
 * a URL somebody can read and edit, and repeated keys are neither. Empty facets
 * are dropped entirely, so an unfiltered garage is `/en/garage` with no trailing
 * `?` — the address that gets shared and the address that gets bookmarked.
 */
export function writeFiltersToSearch(filters) {
    const params = new URLSearchParams();

    FACET_KEYS.forEach((key) => {
        const values = filters?.[key] || [];

        if (values.length > 0) {
            params.set(key, values.join(','));
        }
    });

    return params.toString();
}

/**
 * The filters, held in the address bar.
 *
 * `replace` rather than push: tapping four chips must not cost four presses of
 * the back button to leave the garage. The shelf is one place, and the query is
 * a description of what it is showing rather than a step in a journey.
 */
export function useBrowseFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const search = searchParams.toString();
    const filters = useMemo(() => readFiltersFromSearch(search), [search]);

    const setFilters = useCallback((nextFilters) => {
        setSearchParams(writeFiltersToSearch(nextFilters), { replace: true });
    }, [setSearchParams]);

    return { filters, setFilters, search };
}

/**
 * The app scrolls in `.app-shell__content`, not the window — the same container
 * the shell's own scroll handling reads, looked up the same way.
 */
function scrollContainer() {
    return typeof document === 'undefined' ? null : document.querySelector('.app-shell__content');
}

const SCROLL_MEMORY_KEY = 'legends.garage.scrollTop';

/**
 * Where the shelf was, kept across a RELOAD as well as across a navigation.
 *
 * A module-level number was enough while the only thing that discarded it was
 * closing the tab. It stopped being enough when the update policy started
 * applying a waiting build on a navigation (`autoUpdatePolicy.js`): that reload
 * lands on the address the reader was going to, so the navigation itself costs
 * nothing — but it used to send them back to the top of the garage afterwards,
 * which is the one part of "free" that was not true.
 *
 * SESSION storage rather than local: where somebody had scrolled to an hour ago,
 * in a tab they have since closed, is not a preference — restoring it would be
 * the app remembering something the reader has forgotten.
 *
 * The storage object is a defaulted parameter, as every browser-API touch in
 * this repo is, and every access is guarded. Safari in a private window THROWS
 * on access rather than returning null, and a garage that will not render
 * because it could not recall a scroll position is a far worse failure than one
 * that starts at the top.
 */
function sessionStore(storage) {
    if (storage !== undefined) {
        return storage;
    }

    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
}

export function readRememberedScrollTop(storage) {
    try {
        return Number(sessionStore(storage)?.getItem(SCROLL_MEMORY_KEY)) || 0;
    } catch {
        return 0;
    }
}

export function rememberScrollTop(scrollTop, storage) {
    try {
        sessionStore(storage)?.setItem(SCROLL_MEMORY_KEY, String(Math.round(scrollTop)));
    } catch {
        // Nothing to do, and nothing worth telling the reader about.
    }
}

/**
 * Put the garage back where it was, once there is something to scroll.
 *
 * Restoring is a layout effect so the jump happens before the browser paints; a
 * plain `useEffect` shows the top of the catalogue for one frame first, which
 * reads as the page having reset and then thought better of it. It runs once per
 * mount — after that the position belongs to the reader, not to this record.
 *
 * Recording listens, and its cleanup is a layout effect rather than a passive
 * one. The container this listens to is NOT unmounted with the screen: opening a
 * car empties it and sends it to zero, so a listener that outlives the commit
 * records that zero over the position worth keeping. A layout cleanup has
 * already detached by then; a passive one is deferred past paint and may not
 * have.
 */
export function useRememberedScroll(ready) {
    const restored = useRef(false);

    useLayoutEffect(() => {
        const container = scrollContainer();

        if (!ready || restored.current || !container) {
            return;
        }

        restored.current = true;
        container.scrollTop = readRememberedScrollTop();
    }, [ready]);

    useLayoutEffect(() => {
        const container = scrollContainer();

        if (!container) {
            return undefined;
        }

        const record = () => {
            rememberScrollTop(container.scrollTop);
        };

        container.addEventListener('scroll', record, { passive: true });

        return () => container.removeEventListener('scroll', record);
    }, []);
}

/** Test seam — the record would otherwise carry one spec's scroll into the next. */
export function forgetRememberedScroll() {
    rememberScrollTop(0);
}
